import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Box,
    Typography,
    Chip,
    Paper,
    Stack,
    alpha,
    useTheme,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Tooltip,
    ButtonGroup,
    RadioGroup,
    FormControlLabel,
    Radio,
    Tabs,
    Tab
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Info as InfoIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';
import AdvancedMappingEditor from './AdvancedMappingEditor';

interface Flow {
    id: number;
    name: string;
    is_active: boolean;
    available: boolean;
}

interface Rule {
    conditions: Array<{
        question_id: number;
        answer: string;
    }>;
    value: string;
}

// Definir tipos
type MappingType = 'act_type' | 'conditional_multiple' | 'global_mapping';

interface DynamicTextConfig {
    type: MappingType;
    values: Record<string, string>;
    rules?: Array<{
        conditions: Array<{
            questionId: number;
            option: string;
        }>;
        value: string;
    }>;
}

interface Question {
    id: number;
    text: string;
    options: string[];
    next_question_logic?: Record<string, number>;
    is_final?: boolean;
    article_reference?: string;
    dynamic_text?: Record<string, any>;
    previous_question?: number | null;
    final_message?: string;
    is_success?: boolean;
}

interface FormState extends Partial<Question> {
    dynamic_text?: {
        [key: string]: DynamicTextConfig;
    };
}

interface QuestionFormProps {
    open: boolean;
    onClose: () => void;
    onSave: (question: Partial<Question>) => void;
    question?: Partial<Question>;
    isNew?: boolean;
}

interface ConclusionFormProps {
    open: boolean;
    onClose: () => void;
    onSave: (question: Partial<Question>) => void;
    question?: Partial<Question>;
    isNew?: boolean;
    useExternalDialog?: boolean;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`question-tabpanel-${index}`}
            aria-labelledby={`question-tab-${index}`}
            {...other}
            style={{ padding: '24px 0' }}
        >
            {value === index && (
                <Box>
                    {children}
                </Box>
            )}
        </div>
    );
}

export const QuestionForm = ({
    open,
    onClose,
    onSave,
    question,
    isNew = false,
}: QuestionFormProps) => {
    const theme = useTheme();
    const notification = useNotification();
    const [currentTab, setCurrentTab] = useState(0);
    const [formData, setFormData] = useState<FormState>(
        question || {
            text: '',
            options: [],
            article_reference: '',
            dynamic_text: {}
        }
    );
    
    // Use um useEffect para log e debug
    useEffect(() => {
        console.log("QuestionForm recebeu question:", question);
    }, [question]);
    
    // Adicionar estado para controlar o tipo de mapeamento por variável
    const [variableMappingTypes, setVariableMappingTypes] = useState<Record<string, MappingType>>({});
    
    // Função para inicializar ou atualizar o tipo de mapeamento de uma variável
    const initializeVariableMapping = (variable: string, type: MappingType = 'act_type') => {
        setVariableMappingTypes((prev) => ({
            ...prev,
            [variable]: type
        }));

        setFormData((prev) => {
            const currentDynamicText = { ...(prev.dynamic_text || {}) };
            
            if (!currentDynamicText[variable] || currentDynamicText[variable].type !== type) {
                currentDynamicText[variable] = {
                    type,
                    values: {},
                    ...(type === 'conditional_multiple' ? { rules: [] } : {})
                };
            }
            
            return {
                ...prev,
                dynamic_text: currentDynamicText
            };
        });
    };

    // Função para alterar o tipo de mapeamento de uma variável
    const handleMappingTypeChange = (variable: string, type: MappingType) => {
        setVariableMappingTypes((prev) => ({
            ...prev,
            [variable]: type
        }));
        
        setFormData((prev) => {
            const currentDynamicText = { ...(prev.dynamic_text || {}) };
            
            currentDynamicText[variable] = {
                type,
                values: {},
                ...(type === 'conditional_multiple' ? { rules: [] } : {})
            };
            
            return {
                ...prev,
                dynamic_text: currentDynamicText
            };
        });
    };
    
    // Atualizar função de atualização de valores
    const handleDynamicTextValueChange = (variable: string, actType: string, value: string) => {
        setFormData((prev) => {
            const currentDynamicText = prev.dynamic_text || {};
            const currentConfig = currentDynamicText[variable] || {
                type: 'act_type' as const,
                values: {}
            };
            
            return {
                ...prev,
                dynamic_text: {
                    ...currentDynamicText,
                    [variable]: {
                        ...currentConfig,
                        values: {
                            ...currentConfig.values,
                            [actType]: value
                        }
                    }
                }
            };
        });
    };
    
    // Modificar o useEffect que inicializa a formulário para garantir que o dynamic_text seja preservado
    useEffect(() => {
        if (question) {
            console.log("Atualizando formData com question:", question);
            console.log("Previous question ID:", question.previous_question);
            
            // Verificar se já existe uma configuração de texto dinâmico
            if (question.dynamic_text && typeof question.dynamic_text === 'object') {
                console.log("Question tem dynamic_text:", question.dynamic_text);
                
                // Verificar se há variáveis configuradas
                const hasDynamicTextConfig = Object.keys(question.dynamic_text).some(key => {
                    const value = question.dynamic_text?.[key];
                    return value && typeof value === 'object';
                });
                
                if (hasDynamicTextConfig) {
                    console.log("Configuração de texto dinâmico encontrada");
                    setShowDynamicText(true);
                }
            }
            
            // Garantir que previous_question seja definido corretamente
            setFormData(prev => {
                const newFormData = {
                    ...prev,
                    ...question,
                    previous_question: question.previous_question || null
                };
                console.log("Novo formData:", newFormData);
                return newFormData;
            });
        }
    }, [question]);
    
    const [variables, setVariables] = useState<string[]>([]);
    const [actTypes, setActTypes] = useState<string[]>([]);
    const [previousQuestionOptions, setPreviousQuestionOptions] = useState<string[]>([]);
    const [showDynamicText, setShowDynamicText] = useState<boolean>(false);
    const [mapType, setMapType] = useState<'act_type' | 'conditional_previous_answer'>('act_type');
    
    // Efeito para carregar tipos de ato quando o componente monta
    useEffect(() => {
        const loadActTypes = async () => {
            try {
                console.log('Carregando tipos de ato dos fluxos existentes...');
                
                // Carregar os fluxos existentes
                const response = await api.get('/flows-new');
                console.log('Fluxos carregados:', response.data);
                
                if (response.data && Array.isArray(response.data)) {
                    // Filtrar apenas os fluxos ativos E disponíveis e pegar seus nomes
                    const flowTypes = response.data
                        .filter((flow: any) => {
                            console.log(`Verificando fluxo ${flow.id} - ${flow.name}: is_active=${flow.is_active}, available=${flow.available}`);
                            // Considera available como true se for undefined
                            const isActive = flow.is_active === true;
                            const isAvailable = flow.available === undefined ? true : flow.available === true;
                            console.log(`Fluxo ${flow.id} - ${flow.name}: isActive=${isActive}, isAvailable=${isAvailable}, será incluído=${isActive && isAvailable}`);
                            return isActive && isAvailable;
                        })
                        .map((flow: any) => flow.name)
                        .filter((name: any): name is string => Boolean(name)); // Remove valores vazios/null/undefined
                    
                    if (flowTypes.length > 0) {
                        console.log('Tipos de ato encontrados:', flowTypes);
                        // Ordenar alfabeticamente
                        const sortedFlowTypes = [...flowTypes].sort((a, b) => a.localeCompare(b, 'pt-BR'));
                        console.log('Tipos de ato ordenados:', sortedFlowTypes);
                        setActTypes(sortedFlowTypes);
                    } else {
                        console.log('Nenhum tipo de ato encontrado nos fluxos');
                        setActTypes([]);
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar tipos de ato:', error);
                setActTypes([]);
            }
        };

        loadActTypes();
    }, []);
    
    // No useEffect que detecta questão anterior
    useEffect(() => {
        console.log("Previous question mudou:", formData.previous_question);
        if (formData.previous_question) {
            console.log("Questão anterior detectada, ID:", formData.previous_question);
            loadPreviousQuestionOptions(formData.previous_question);
        }
    }, [formData.previous_question]);
    
    // Modificar os hooks useMemo para remover possiblePreviousQuestions
    const previousQuestionsByFlow = useMemo(() => {
        // Como não temos mais possiblePreviousQuestions, retornamos um objeto vazio
        return {};
    }, []);
    
    // Listar todos os fluxos disponíveis
    const availableFlows = useMemo(() => {
        // Como não temos mais possiblePreviousQuestions, retornamos um array vazio
        return [];
    }, []);
    
    // Extrair função de carregamento de opções da questão anterior
    const loadPreviousQuestionOptions = async (questionId: number) => {
        try {
            console.log(`Carregando opções da questão anterior (ID: ${questionId})...`);
            const response = await api.get(`/question-bank/${questionId}`);
            
            if (response.data && Array.isArray(response.data.options)) {
                console.log('Opções da questão anterior:', response.data.options);
                setPreviousQuestionOptions(response.data.options);
            } else {
                console.log('Nenhuma opção encontrada na questão anterior');
                setPreviousQuestionOptions([]);
            }
        } catch (error) {
            console.error('Erro ao carregar opções da questão anterior:', error);
            setPreviousQuestionOptions([]);
        }
    };

    const extractVariables = (text: string) => {
        const matches = text.match(/\{([^}]+)\}/g) || [];
        return matches.map(match => match.slice(1, -1));
    };

    // Adicionar função para normalizar o formato do dynamic_text
    const normalizeDynamicText = (dynamicText: Record<string, any>, extractedVariables: string[]): Record<string, any> => {
        if (!dynamicText) return {};
        
        console.log('Normalizando dynamic_text:', dynamicText);
        const normalized: Record<string, any> = {};
        
        extractedVariables.forEach(variable => {
            if (dynamicText[variable]) {
                const config = dynamicText[variable];
                
                if (config.type === 'conditional_multiple') {
                    normalized[variable] = {
                        type: config.type,
                        rules: config.rules || [] // Garantir que rules existe
                    };
                } else if (config.type) {
                    normalized[variable] = {
                        type: config.type,
                        values: config.values || {}
                    };
                }
            }
        });
        
        return normalized;
    };

    // No useEffect que normaliza o dynamic_text
    useEffect(() => {
        // Se for uma questão existente e tiver dynamic_text
        if (!isNew && question?.dynamic_text) {
            console.log('Question tem dynamic_text:', question.dynamic_text);
            
            // Extrair variáveis do texto
            const extractedVars = extractVariables(formData.text || '');
            
            // Normalizar o formato do dynamic_text
            const normalizedDynamicText = normalizeDynamicText(question.dynamic_text, extractedVars);
            
            // Verificar se há configuração de texto dinâmico
            if (Object.keys(normalizedDynamicText).length > 0) {
                console.log('Configuração de texto dinâmico encontrada');
                setShowDynamicText(true);
                
                // Verificar o tipo predominante
                const hasPreviousAnswerMapping = Object.values(normalizedDynamicText).some(
                    (config: any) => config.type === 'conditional_previous_answer'
                );
                
                setMapType(hasPreviousAnswerMapping ? 'conditional_previous_answer' : 'act_type');
                
                // Atualizar o formData com o dynamic_text normalizado
                setFormData(prev => ({
                    ...prev,
                    dynamic_text: normalizedDynamicText
                }));
            }
        }
    }, [isNew, question?.dynamic_text]);

    // Adicionar novamente o useEffect para extrair variáveis
    useEffect(() => {
        // Extrair variáveis do texto da questão
        if (formData.text) {
            const newVariables = extractVariables(formData.text);
            
            // Só atualizar se as variáveis mudaram
            if (JSON.stringify(newVariables) !== JSON.stringify(variables)) {
                console.log(`Variáveis extraídas: ${JSON.stringify(newVariables)}`);
                setVariables(newVariables);
                
                // Se não estiver em modo de edição, inicializar o texto dinâmico
                if (isNew && !formData.dynamic_text) {
                    // Inicializar texto dinâmico para novas variáveis
                    const initialDynamicText: Record<string, any> = {};
                    
                    newVariables.forEach(variable => {
                        initialDynamicText[variable] = {
                            type: 'act_type',
                            values: {}
                        };
                    });
                    
                    setFormData(prev => ({
                        ...prev,
                        dynamic_text: initialDynamicText
                    }));
                }
            }
        }
    }, [formData.text, isNew, variables]);

    // Adicionar useEffect para inicializar o tipo de mapeamento quando variáveis são detectadas
    useEffect(() => {
        // Quando novas variáveis são detectadas
        variables.forEach((variable: string) => {
            if (!formData.dynamic_text?.[variable]) {
                // Inicializar com o tipo padrão
                handleMappingTypeChange(variable, 'act_type');
            }
        });
    }, [variables]);

    // Adicionar função para verificar se uma variável tem valores configurados
    const hasConfiguredValues = (variable: string): boolean => {
        const config = formData.dynamic_text?.[variable] as DynamicTextConfig | undefined;
        if (!config) return false;
        
        if (config.type === 'conditional_multiple' && config.rules) {
            return config.rules.some(rule => rule.value && typeof rule.value === 'string' && rule.value.trim() !== '');
        }
        
        if (config.values) {
            return Object.values(config.values).some(value => 
                value && typeof value === 'string' && value.trim() !== ''
            );
        }
        
        return false;
    };

    const handleChange = (field: keyof Question, value: any) => {
        setFormData((prev: FormState) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleAddOption = () => {
        const newOption = document.getElementById('new-option') as HTMLInputElement;
        if (newOption && newOption.value.trim()) {
            setFormData((prev) => ({
                ...prev,
                options: [...(prev.options || []), newOption.value.trim()],
            }));
            newOption.value = '';
        }
    };

    const handleRemoveOption = (index: number) => {
        setFormData((prev) => {
            const newOptions = [...(prev.options || [])];
            newOptions.splice(index, 1);
        
            // Atualizar também a lógica de próxima questão
            const updatedLogic = { ...prev.next_question_logic };
        delete updatedLogic[index.toString()];

            return {
                ...prev,
                options: newOptions,
                next_question_logic: updatedLogic,
            };
        });
    };

    const handleQuestionLogicChange = (option: string, questionId: string) => {
        const id = questionId ? parseInt(questionId) : null;
        const updatedLogic = { ...formData.next_question_logic, [option]: id };
        handleChange('next_question_logic', updatedLogic);
    };

    const handleSave = () => {
        // Validar campos obrigatórios
        if (!formData.text || typeof formData.text !== 'string' || !formData.text.trim()) {
            notification.showError('Preencha o texto da questão');
            return;
        }

        // Para questões não finais, validar opções
        if (!formData.is_final && (!formData.options || formData.options.length === 0)) {
            notification.showError('Adicione pelo menos uma opção de resposta');
            return;
        }

        // Verificar se todas as variáveis foram configuradas
        const unconfiguredVariables = variables.filter((variable: string) => !hasConfiguredValues(variable));
        if (unconfiguredVariables.length > 0) {
            if (window.confirm(
                `As seguintes variáveis não foram configuradas: ${unconfiguredVariables.map((v: string) => `{${v}}`).join(', ')}. Deseja salvar mesmo assim?`
            )) {
                saveQuestion();
            }
            return;
        }

        saveQuestion();
    };

    // Função auxiliar para salvar a questão
    const saveQuestion = () => {
        console.log('Salvando questão:', formData);

        const cleanedDynamicText: Record<string, DynamicTextConfig> = {};

        variables.forEach((variable: string) => {
            if (formData.dynamic_text?.[variable]) {
                const config = formData.dynamic_text[variable];
                
                if (config.type === 'act_type') {
                    cleanedDynamicText[variable] = {
                        type: config.type,
                        values: config.values || {}
                    };
                } else if (config.type === 'conditional_multiple') {
                    cleanedDynamicText[variable] = {
                        type: config.type,
                        values: {}, // Adicionar values vazio para satisfazer a tipagem
                        rules: config.rules || []
                    };
                }
            }
        });

        const questionToSave: Partial<Question> = {
            ...formData,
            dynamic_text: cleanedDynamicText
        };

        onSave(questionToSave);
        onClose();
    };

    // Adicionar função para gerar preview do texto
    const generatePreview = (text: string, variable: string, type: 'act_type' | 'conditional_previous_answer', context: any) => {
        if (!text || !variable || !formData.dynamic_text?.[variable]) return text;

        let result = text;
        const config = formData.dynamic_text[variable];
        
        if (type === 'act_type') {
            // Para cada tipo de ato, mostrar como o texto ficará
            actTypes.forEach((actType: string) => {
                const value = config.values?.[actType] || `{${variable}}`;
                result = result.replace(new RegExp(`\\{${variable}\\}`, 'g'), value);
            });
        } else if (type === 'conditional_previous_answer' && formData.previous_question) {
            // Para cada opção da questão anterior, mostrar como o texto ficará
            previousQuestionOptions.forEach((option: string) => {
                const value = config.values?.[`previous_${option}`] || `{${variable}}`;
                result = result.replace(new RegExp(`\\{${variable}\\}`, 'g'), value);
            });
        }
        
        return result;
    };

    // Adicionar função para verificar se tem questão anterior
    const checkPreviousQuestion = async () => {
        if (!question?.id) return;
        
        try {
            console.log("Verificando questão anterior...");
            
            // Usar o novo endpoint para buscar a questão anterior
            const response = await api.get(`/flows-new/flow-questions/${question.id}/previous`);
            console.log("Questão anterior:", response.data);
            
            if (response.data) {
                // Atualizar o formData com a questão anterior
                setFormData(prev => {
                    const newFormData = {
                        ...prev,
                        previous_question: response.data.id
                    };
                    console.log("Atualizando formData com questão anterior:", newFormData);
                    return newFormData;
                });
                
                // Definir as opções da questão anterior
                if (response.data.options && Array.isArray(response.data.options)) {
                    console.log("Definindo opções da questão anterior:", response.data.options);
                    setPreviousQuestionOptions(response.data.options);
                }
            }
        } catch (error: any) {
            // Se não encontrar questão anterior, é um comportamento esperado
            if (error.response?.status === 404) {
                console.log("Nenhuma questão anterior encontrada");
            } else {
                console.error("Erro ao verificar questão anterior:", error);
            }
        }
    };

    // Adicionar useEffect para verificar questão anterior quando o componente montar
    useEffect(() => {
        if (!isNew && question?.id) {
            checkPreviousQuestion();
        }
    }, [isNew, question?.id]);

    // Atualizar o useEffect que monitora previous_question
    useEffect(() => {
        console.log("Previous question mudou:", formData.previous_question);
        if (formData.previous_question) {
            console.log("Questão anterior detectada, ID:", formData.previous_question);
            loadPreviousQuestionOptions(formData.previous_question);
            
            // Habilitar a seção de texto dinâmico automaticamente
            setShowDynamicText(true);
            
            // Se já tivermos configurações de mapeamento por resposta anterior, use-as
            if (Object.values(formData.dynamic_text || {}).some(
                (config: any) => config.type === 'conditional_previous_answer'
            )) {
                setMapType('conditional_previous_answer');
            }
        }
    }, [formData.previous_question]);

    // Adicionar componente de informação da questão anterior
    const PreviousQuestionInfo = () => {
        if (!formData.previous_question) return null;

    return (
            <Box sx={{ mt: 2, mb: 2 }}>
                <Alert 
                    severity="info"
                    icon={<InfoIcon />}
                    sx={{ 
                        '& .MuiAlert-message': { 
                            display: 'flex', 
                            alignItems: 'center',
                            gap: 1
                        }
                    }}
                >
                    <Box>
                        <Typography variant="subtitle2">
                            Questão Anterior Conectada (ID: {formData.previous_question})
                        </Typography>
                        {previousQuestionOptions.length > 0 && (
                            <Typography variant="body2" color="textSecondary">
                                Opções disponíveis: {previousQuestionOptions.join(', ')}
                            </Typography>
                        )}
                    </Box>
                </Alert>
            </Box>
        );
    };

    // No QuestionForm, adicionar estado para questões anteriores
    const [previousQuestions, setPreviousQuestions] = useState<Question[]>([]);

    // No QuestionForm, modificar o useEffect para buscar questões anteriores
    useEffect(() => {
        const fetchPreviousQuestions = async () => {
            try {
                // Se for uma questão existente, busca pelo ID
                if (question?.id) {
                    const response = await api.get(`/flows-new/flow-questions/${question.id}/previous-all`);
                    console.log('Questões anteriores para edição:', response.data);
                    
                    if (Array.isArray(response.data)) {
                        setPreviousQuestions(response.data);
                    }
                } 
                // Se for uma nova questão, buscar todas as questões disponíveis
                else if (isNew) {
                    // Buscar todas as questões do banco
                    const response = await api.get('/question-bank');
                    console.log('Todas as questões disponíveis:', response.data);
                    
                    if (Array.isArray(response.data)) {
                        // Filtrar apenas questões (não conclusões)
                        const availableQuestions = response.data
                            .filter((q: any) => !q.is_final)
                            .map((q: any) => {
                                // Converter options de string para array se necessário
                                let options = q.options;
                                if (typeof options === 'string') {
                                    try {
                                        options = JSON.parse(options);
                                    } catch {
                                        options = [options];
                                    }
                                }
                                
                                return {
                                    id: q.id,
                                    text: q.text,
                                    options: options
                                };
                            });
                        
                        setPreviousQuestions(availableQuestions);
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar questões anteriores:', error);
            }
        };

        fetchPreviousQuestions();
    }, [question?.id, isNew]);

    // Adicionar useEffect para depuração
    useEffect(() => {
        if (formData.dynamic_text) {
            console.log("Mapeamentos de texto dinâmico carregados:", formData.dynamic_text);
            
            // Verificar se há mapeamentos do tipo conditional_multiple
            Object.entries(formData.dynamic_text || {}).forEach(([variable, config]: [string, any]) => {
                if (config.type === 'conditional_multiple' && config.rules) {
                    console.log(`Mapeamento avançado para variável ${variable}:`, config.rules);
                }
            });
        }
    }, [formData.dynamic_text]);

    return (
        <Dialog 
            open={open}
            maxWidth="md"
            fullWidth
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle sx={{ 
                borderBottom: '1px solid',
                borderColor: theme.palette.divider,
                pb: 2
            }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                {isNew ? 'Nova Questão' : 'Editar Questão'}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs 
                            value={currentTab} 
                            onChange={(e, newValue) => setCurrentTab(newValue)}
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500
                                }
                            }}
                        >
                            <Tab label="Informações Básicas" />
                            <Tab label="Texto Dinâmico" />
                        </Tabs>
                    </Box>

                    <TabPanel value={currentTab} index={0}>
                        <Stack spacing={3}>
                    <TextField
                        label="Texto da Questão"
                        value={formData.text}
                        onChange={(e) => handleChange('text', e.target.value)}
                        multiline
                                rows={4}
                        fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.background.paper, 0.5)
                                    }
                                }}
                            />

                            <Box>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                                    Opções de Resposta
                                </Typography>
                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        id="new-option"
                                        placeholder="Digite uma opção e pressione Enter"
                                        fullWidth
                                        size="small"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddOption();
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: alpha(theme.palette.background.paper, 0.5)
                                            }
                                        }}
                                    />
                                </Box>
                                <Box 
                                    sx={{ 
                                        display: 'flex', 
                                        flexWrap: 'wrap',
                                        gap: 1,
                                        minHeight: '50px',
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: alpha(theme.palette.background.paper, 0.7),
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.1)
                                    }}
                                >
                                    {formData.options?.map((option: string, index: number) => (
                                        <Chip
                                            key={index}
                                            label={option}
                                            onDelete={() => handleRemoveOption(index)}
                                            sx={{
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                borderRadius: '16px',
                                                '& .MuiChip-label': {
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 500
                                                },
                                                '& .MuiChip-deleteIcon': {
                                                    color: theme.palette.primary.main,
                                                    '&:hover': {
                                                        color: theme.palette.error.main
                                                    }
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>

                        <TextField
                            label="Mensagem Final"
                            value={formData.final_message}
                            onChange={(e) => handleChange('final_message', e.target.value)}
                            multiline
                                rows={4}
                            fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.background.paper, 0.5)
                                    }
                                }}
                            />

                                    <TextField
                                label="Referência do Artigo"
                                value={formData.article_reference}
                                onChange={(e) => handleChange('article_reference', e.target.value)}
                                        fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.background.paper, 0.5)
                                    }
                                }}
                            />
                        </Stack>
                    </TabPanel>

                    <TabPanel value={currentTab} index={1}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                border: '1px solid',
                                borderColor: alpha(theme.palette.primary.main, 0.1),
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                                Configuração de Texto Dinâmico
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Configure as substituições de texto dinâmico baseadas em condições ou respostas anteriores.
                            </Typography>
                            
                            {extractVariables(formData.text || '').length > 0 ? (
                                <Stack spacing={3}>
                                    {extractVariables(formData.text || '').map((variable, index) => (
                                        <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.2), borderRadius: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                                                Variável: {`{${variable}}`}
                                            </Typography>
                                            
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Selecione o tipo de mapeamento para esta variável:
                                                </Typography>
                                                
                                                <RadioGroup
                                                    row
                                                    value={formData.dynamic_text?.[variable]?.type || 'act_type'}
                                                    onChange={(e) => {
                                                        const newType = e.target.value as 'act_type' | 'conditional_multiple' | 'global_mapping';
                                                        
                                                        // Debug log
                                                        console.log(`Alterando tipo para ${newType} para variável ${variable}`);
                                                        
                                                        // Criar estrutura de dados inicial com base no tipo selecionado
                                                        const newDynamicText = formData.dynamic_text ? { ...formData.dynamic_text } : {};
                                                        
                                                        if (newType === 'act_type') {
                                                            newDynamicText[variable] = {
                                                                type: 'act_type',
                                                                values: formData.dynamic_text?.[variable]?.values || {}
                                                            };
                                                        } else if (newType === 'conditional_multiple') {
                                                            newDynamicText[variable] = {
                                                                type: 'conditional_multiple',
                                                                values: {},
                                                                rules: [
                                                                    {
                                                                        conditions: [],
                                                                        value: '' 
                                                                    }
                                                                ]
                                                            };
                                                            
                                                            console.log("Estrutura inicial para mapeamento avançado:", newDynamicText[variable]);
                                                        } else if (newType === 'global_mapping') {
                                                            newDynamicText[variable] = {
                                                                type: 'global_mapping',
                                                                values: {}
                                                            };
                                                        }
                                                        
                                                        // Atualizar estado
                                                        setFormData({
                                                            ...formData,
                                                            dynamic_text: newDynamicText
                                                        });
                                                    }}
                                                    sx={{ 
                                                        mb: 2,
                                                        '& .MuiFormControlLabel-root': {
                                                            marginRight: 4,
                                                            '& .MuiRadio-root': {
                                                                color: theme.palette.primary.main
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="act_type" 
                                                        control={<Radio />} 
                                                        label={
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                Mapear por Tipo de Ato
                                                            </Typography>
                                                        }
                                                    />
                                                    <FormControlLabel 
                                                        value="conditional_multiple" 
                                                        control={<Radio />} 
                                                        label={
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                Mapeamento Avançado
                                                            </Typography>
                                                        }
                                                    />
                                                    <FormControlLabel 
                                                        value="global_mapping" 
                                                        control={<Radio />} 
                                                        label={
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                Usar Mapeamento Global
                                                            </Typography>
                                                        }
                                                    />
                                                </RadioGroup>
                                            </Box>
                                            
                                            {/* Mapeamento por Tipo de Ato */}
                                            {formData.dynamic_text?.[variable]?.type === 'act_type' && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Mapeamento por tipo de ato:
                                                    </Typography>
                                                    <Stack spacing={2}>
                                                        {actTypes.map((actType, actIndex) => (
                                                            <TextField
                                                                key={actIndex}
                                                                label={`Valor para "${actType}"`}
                                                                value={formData.dynamic_text?.[variable]?.values?.[actType] || ''}
                                                                onChange={(e) => {
                                                                    console.log(`Configurando valor para variável ${variable} para o tipo de ato "${actType}": ${e.target.value}`);
                                                                    
                                                                    setFormData((prev: FormState) => ({
                                                                        ...prev,
                                                                        dynamic_text: {
                                                                            ...prev.dynamic_text,
                                                                            [variable]: {
                                                                                type: 'act_type',
                                                                                values: {
                                                                                    ...prev.dynamic_text?.[variable]?.values || {},
                                                                                    [actType]: e.target.value
                                                                                }
                                                                            }
                                                                        }
                                                                    }));
                                                                }}
                                                                size="small"
                                                                fullWidth
                                                                variant="outlined"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 1.5,
                                                                        backgroundColor: alpha(theme.palette.background.paper, 0.8)
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                                            
                                            {/* Mapeamento Avançado */}
                                            {formData.dynamic_text?.[variable]?.type === 'conditional_multiple' && (
                                                <Box sx={{ mt: 2, p: 0, borderRadius: 2, overflow: 'hidden' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Configuração de mapeamento avançado:
                                                    </Typography>
                                                    
                                                    <AdvancedMappingEditor
                                                        previousQuestions={previousQuestions}
                                                        currentMappings={formData.dynamic_text?.[variable] || { 
                                                            type: 'conditional_multiple', 
                                                            values: {}, 
                                                            rules: [{ conditions: [], value: '' }] 
                                                        }}
                                                        onMappingsChange={(newMappings) => {
                                                            console.log("Recebendo novos mapeamentos do editor avançado:", newMappings);
                                                        
                                                            setFormData((prev) => {
                                                                // Garantir que newMappings tenha a estrutura correta
                                                                const typedMappings: DynamicTextConfig = {
                                                                    ...newMappings,
                                                                    type: 'conditional_multiple',
                                                                    values: newMappings.values || {}
                                                                };
                                                                
                                                                console.log("Atualizando formData com novo mapeamento:", typedMappings);
                                                                
                                                                return {
                                                                    ...prev,
                                                                    dynamic_text: {
                                                                        ...prev.dynamic_text,
                                                                        [variable]: typedMappings
                                                                    }
                                                                };
                                                            });
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            
                                            {/* Mapeamento Global */}
                                            {formData.dynamic_text?.[variable]?.type === 'global_mapping' && (
                                                <Box sx={{ mt: 2, p: 3, borderRadius: 2, bgcolor: alpha('#f5f5f5', 0.5) }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Esta variável usará o mapeamento global definido nas configurações.
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Não é necessário configurar valores adicionais aqui.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Alert severity="info">
                                    Nenhuma variável encontrada no texto da questão. 
                                    Adicione variáveis utilizando {"{nome_da_variavel}"} no texto da questão.
                                </Alert>
                            )}
                        </Paper>
                    </TabPanel>
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: theme.palette.divider }}>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none'
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={() => onSave(formData)}
                    variant="contained"
                    disabled={!formData.text || !formData.options?.length}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none'
                    }}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    );
}; 

// Componente para criar/editar uma conclusão
export const ConclusionForm = React.memo(({
    open,
    onClose,
    onSave,
    question,
    isNew = false,
    useExternalDialog = false,
}: ConclusionFormProps) => {
    const theme = useTheme();
    const { showError } = useNotification();
    
    // Adicionar log para depuração
    console.log("ConclusionForm recebeu question:", question);
    
    const [currentTab, setCurrentTab] = useState(0);
    const [formData, setFormData] = useState<Partial<Question>>(
        question || {
            text: '',
            final_message: '',
            article_reference: '',
            is_success: undefined,
            is_final: true,
            options: [], // Mantemos isso vazio, mas não exibimos na interface
            dynamic_text: {}
        }
    );
    
    // Atualizar formData quando question mudar
    useEffect(() => {
        if (question) {
            console.log("Atualizando formData com question:", question);
            setFormData(question);
        }
    }, [question]);
    
    // Adicionar log para depuração do formData
    useEffect(() => {
        console.log("ConclusionForm formData:", formData);
    }, [formData]);

    // No ConclusionForm, adicionar o mesmo estado e useEffect
    const [previousQuestions, setPreviousQuestions] = useState<Question[]>([]);

    useEffect(() => {
        const fetchPreviousQuestions = async () => {
            try {
                // Se for uma conclusão existente, busca pelo ID
                const endpoint = isNew 
                    ? '/flows-new/flow-questions/previous-all' 
                    : `/flows-new/flow-questions/${question?.id}/previous-all`;
                
                console.log(`Buscando questões anteriores para conclusão: ${endpoint}`);
                const response = await api.get(endpoint);
                
                if (response.data) {
                    console.log('Questões anteriores para conclusão:', response.data);
                    
                    // Processar as questões anteriores
                    const availableQuestions = response.data
                        .filter((q: any) => q.id !== question?.id) // Filtrar a própria questão
                        .map((q: any) => {
                            // Garantir que options seja um array
                            let options = q.options;
                            if (!Array.isArray(options)) {
                                try {
                                    options = JSON.parse(options);
                                } catch {
                                    options = [options];
                                }
                            }
                            
                            return {
                                id: q.id,
                                text: q.text,
                                options: options,
                                flow_name: q.flow_name // Garantir que o nome do fluxo seja incluído
                            };
                        });
                    
                    setPreviousQuestions(availableQuestions);
                }
            } catch (error) {
                console.error('Erro ao buscar questões anteriores para conclusão:', error);
            }
        };

        fetchPreviousQuestions();
    }, [question?.id, isNew]);

    const handleChange = (field: string, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleConclusionClick = (isSuccess: boolean) => {
        handleChange('is_success', isSuccess);
    };

    // Verificar se tanto o título quanto a mensagem final estão preenchidos
    const isSaveEnabled = formData?.text?.trim() && formData?.final_message?.trim() && formData.is_success !== undefined;

    const handleSaveClick = () => {
        if (!formData.text || typeof formData.text !== 'string' || !formData.text.trim()) {
            showError('Preencha o título da conclusão');
            return;
        }
        
        if (!formData.final_message || typeof formData.final_message !== 'string' || !formData.final_message.trim()) {
            showError('Preencha a mensagem final');
            return;
        }

        if (formData.is_success === undefined) {
            showError('Selecione se a conclusão é positiva ou negativa');
            return;
        }

        onSave({
            ...formData,
            is_final: true,
            options: [] // Garantir que não tenha opções
        });
    };

    // Extrair variáveis tanto do título quanto da mensagem final
    const extractVariables = (text: string) => {
        const matches = text.match(/\{([^}]+)\}/g) || [];
        return matches.map(match => match.slice(1, -1));
    };

    // No ConclusionForm, depois do useEffect para fetchPreviousQuestions
    // Adicionar state e useEffect para actTypes
    const [actTypes, setActTypes] = useState<string[]>([]);
    const [previousQuestionOptions, setPreviousQuestionOptions] = useState<string[]>([]);

    // Efeito para carregar tipos de ato quando o componente monta
    useEffect(() => {
        const loadActTypes = async () => {
            try {
                console.log('Carregando tipos de ato para conclusão...');
                
                // Carregar os fluxos existentes
                const response = await api.get('/flows-new');
                console.log('Fluxos carregados para conclusão:', response.data);
                
                if (response.data && Array.isArray(response.data)) {
                    // Filtrar apenas os fluxos ativos E disponíveis e pegar seus nomes
                    const flowTypes = response.data
                        .filter((flow: any) => {
                            console.log(`Verificando fluxo ${flow.id} - ${flow.name}: is_active=${flow.is_active}, available=${flow.available}`);
                            // Verifica explicitamente se available é true
                            return flow.is_active === true && flow.available === true;
                        })
                        .map((flow: any) => flow.name)
                        .filter((name: any): name is string => Boolean(name));
                    
                    if (flowTypes.length > 0) {
                        console.log('Tipos de ato encontrados para conclusão:', flowTypes);
                        // Ordenar alfabeticamente
                        const sortedFlowTypes = [...flowTypes].sort((a, b) => a.localeCompare(b, 'pt-BR'));
                        console.log('Tipos de ato ordenados para conclusão:', sortedFlowTypes);
                        setActTypes(sortedFlowTypes);
                    } else {
                        console.log('Nenhum tipo de ato encontrado nos fluxos para conclusão');
                        setActTypes([]);
                    }
                }
            } catch (error) {
                console.error('Erro ao carregar tipos de ato para conclusão:', error);
                setActTypes([]);
            }
        };

        loadActTypes();
    }, []);

    // Adicionar useEffect para carregar opções da questão anterior quando necessário
    useEffect(() => {
        if (previousQuestions.length > 0 && formData.dynamic_text) {
            const allOptions: string[] = [];
            previousQuestions.forEach((question: Question) => {
                if (question.options && Array.isArray(question.options)) {
                    question.options.forEach((option: string) => {
                        if (!allOptions.includes(option)) {
                            allOptions.push(option);
                        }
                    });
                }
            });
            
            console.log('Opções encontradas de questões anteriores:', allOptions);
            setPreviousQuestionOptions(allOptions);
        }
    }, [previousQuestions, formData.dynamic_text]);

    // Renderizar o conteúdo do formulário
    const renderContent = () => (
        <>
            <DialogTitle sx={{ 
                borderBottom: '1px solid',
                borderColor: theme.palette.divider,
                pb: 2
            }}>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                    {isNew ? 'Nova Conclusão' : 'Editar Conclusão'}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs 
                            value={currentTab} 
                            onChange={(e, newValue) => setCurrentTab(newValue)}
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500
                                }
                            }}
                        >
                            <Tab label="Informações Básicas" />
                            <Tab label="Texto Dinâmico" />
                        </Tabs>
                    </Box>

                    <TabPanel value={currentTab} index={0}>
                        <Stack spacing={3}>
                            <TextField
                                label="Título da Conclusão"
                                value={formData.text}
                                onChange={(e) => handleChange('text', e.target.value)}
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.background.paper, 0.5)
                                    }
                                }}
                            />

                            <TextField
                                label="Mensagem Final"
                                value={formData.final_message}
                                onChange={(e) => handleChange('final_message', e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.background.paper, 0.5)
                                    }
                                }}
                            />

                            <TextField
                                label="Referência do Artigo"
                                value={formData.article_reference}
                                onChange={(e) => handleChange('article_reference', e.target.value)}
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: alpha(theme.palette.background.paper, 0.5)
                                    }
                                }}
                            />
                            
                            <Box sx={{ 
                                p: 3, 
                                border: '1px solid', 
                                borderColor: theme.palette.divider,
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.background.paper, 0.5)
                            }}>
                                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                                    Tipo de Conclusão
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Selecione se esta conclusão representa um resultado positivo ou negativo.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant={formData.is_success === true ? "contained" : "outlined"}
                                        color="success"
                                        onClick={() => handleConclusionClick(true)}
                                        startIcon={<CheckCircleIcon />}
                                        sx={{ 
                                            flex: 1,
                                            py: 1.5,
                                            borderRadius: 2,
                                            borderWidth: 2,
                                            textTransform: 'none'
                                        }}
                                    >
                                        Positiva
                                    </Button>
                                    <Button
                                        variant={formData.is_success === false ? "contained" : "outlined"}
                                        color="error"
                                        onClick={() => handleConclusionClick(false)}
                                        startIcon={<CancelIcon />}
                                        sx={{ 
                                            flex: 1,
                                            py: 1.5,
                                            borderRadius: 2,
                                            borderWidth: 2,
                                            textTransform: 'none'
                                        }}
                                    >
                                        Negativa
                                    </Button>
                                </Box>
                            </Box>
                        </Stack>
                    </TabPanel>

                    <TabPanel value={currentTab} index={1}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 3, 
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                border: '1px solid',
                                borderColor: alpha(theme.palette.primary.main, 0.1),
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                                Configuração de Texto Dinâmico
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Configure as substituições de texto dinâmico baseadas em condições ou respostas anteriores.
                            </Typography>
                            
                            {extractVariables(formData.text || '').length > 0 || extractVariables(formData.final_message || '').length > 0 ? (
                                <Stack spacing={3}>
                                    {Array.from(new Set([
                                        ...extractVariables(formData.text || ''),
                                        ...extractVariables(formData.final_message || '')
                                    ])).map((variable, index) => (
                                        <Box key={index} sx={{ p: 2, border: '1px solid', borderColor: alpha(theme.palette.divider, 0.2), borderRadius: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                                                Variável: {`{${variable}}`}
                                            </Typography>
                                            
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Selecione o tipo de mapeamento para esta variável:
                                                </Typography>
                                                
                                                <RadioGroup
                                                    row
                                                    value={formData.dynamic_text?.[variable]?.type || 'act_type'}
                                                    onChange={(e) => {
                                                        const newType = e.target.value as 'act_type' | 'conditional_multiple' | 'global_mapping';
                                                        
                                                        // Debug log
                                                        console.log(`Alterando tipo para ${newType} para variável ${variable}`);
                                                        
                                                        // Criar estrutura de dados inicial com base no tipo selecionado
                                                        const newDynamicText = formData.dynamic_text ? { ...formData.dynamic_text } : {};
                                                        
                                                        if (newType === 'act_type') {
                                                            newDynamicText[variable] = {
                                                                type: 'act_type',
                                                                values: formData.dynamic_text?.[variable]?.values || {}
                                                            };
                                                        } else if (newType === 'conditional_multiple') {
                                                            newDynamicText[variable] = {
                                                                type: 'conditional_multiple',
                                                                values: {},
                                                                rules: [
                                                                    {
                                                                        conditions: [],
                                                                        value: '' 
                                                                    }
                                                                ]
                                                            };
                                                            
                                                            console.log("Estrutura inicial para mapeamento avançado:", newDynamicText[variable]);
                                                        } else if (newType === 'global_mapping') {
                                                            newDynamicText[variable] = {
                                                                type: 'global_mapping',
                                                                values: {}
                                                            };
                                                        }
                                                        
                                                        // Atualizar estado
                                                        setFormData({
                                                            ...formData,
                                                            dynamic_text: newDynamicText
                                                        });
                                                    }}
                                                    sx={{ 
                                                        mb: 2,
                                                        '& .MuiFormControlLabel-root': {
                                                            marginRight: 4,
                                                            '& .MuiRadio-root': {
                                                                color: theme.palette.primary.main
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="act_type" 
                                                        control={<Radio />} 
                                                        label={
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                Mapear por Tipo de Ato
                                                            </Typography>
                                                        }
                                                    />
                                                    <FormControlLabel 
                                                        value="conditional_multiple" 
                                                        control={<Radio />} 
                                                        label={
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                Mapeamento Avançado
                                                            </Typography>
                                                        }
                                                    />
                                                    <FormControlLabel 
                                                        value="global_mapping" 
                                                        control={<Radio />} 
                                                        label={
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                Usar Mapeamento Global
                                                            </Typography>
                                                        }
                                                    />
                                                </RadioGroup>
                                            </Box>
                                            
                                            {/* Mapeamento por Tipo de Ato */}
                                            {formData.dynamic_text?.[variable]?.type === 'act_type' && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Mapeamento por tipo de ato:
                                                    </Typography>
                                                    <Stack spacing={2}>
                                                        {actTypes.map((actType, actIndex) => (
                                                            <TextField
                                                                key={actIndex}
                                                                label={`Valor para "${actType}"`}
                                                                value={formData.dynamic_text?.[variable]?.values?.[actType] || ''}
                                                                onChange={(e) => {
                                                                    console.log(`Configurando valor para variável ${variable} para o tipo de ato "${actType}": ${e.target.value}`);
                                                                    
                                                                    setFormData((prev: FormState) => ({
                                                                        ...prev,
                                                                        dynamic_text: {
                                                                            ...prev.dynamic_text,
                                                                            [variable]: {
                                                                                type: 'act_type',
                                                                                values: {
                                                                                    ...prev.dynamic_text?.[variable]?.values || {},
                                                                                    [actType]: e.target.value
                                                                                }
                                                                            }
                                                                        }
                                                                    }));
                                                                }}
                                                                size="small"
                                                                fullWidth
                                                                variant="outlined"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 1.5,
                                                                        backgroundColor: alpha(theme.palette.background.paper, 0.8)
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            )}
                                            
                                            {/* Mapeamento Avançado */}
                                            {formData.dynamic_text?.[variable]?.type === 'conditional_multiple' && (
                                                <Box sx={{ mt: 2, p: 0, borderRadius: 2, overflow: 'hidden' }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Configuração de mapeamento avançado:
                                                    </Typography>
                                                    
                                                    <AdvancedMappingEditor
                                                        previousQuestions={previousQuestions}
                                                        currentMappings={formData.dynamic_text?.[variable] || { 
                                                            type: 'conditional_multiple', 
                                                            values: {}, 
                                                            rules: [{ conditions: [], value: '' }] 
                                                        }}
                                                        onMappingsChange={(newMappings) => {
                                                            console.log("Recebendo novos mapeamentos do editor avançado:", newMappings);
                                                        
                                                            setFormData((prev) => {
                                                                // Garantir que newMappings tenha a estrutura correta
                                                                const typedMappings: DynamicTextConfig = {
                                                                    ...newMappings,
                                                                    type: 'conditional_multiple',
                                                                    values: newMappings.values || {}
                                                                };
                                                                
                                                                console.log("Atualizando formData com novo mapeamento:", typedMappings);
                                                                
                                                                return {
                                                                    ...prev,
                                                                    dynamic_text: {
                                                                        ...prev.dynamic_text,
                                                                        [variable]: typedMappings
                                                                    }
                                                                };
                                                            });
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            
                                            {/* Mapeamento Global */}
                                            {formData.dynamic_text?.[variable]?.type === 'global_mapping' && (
                                                <Box sx={{ mt: 2, p: 3, borderRadius: 2, bgcolor: alpha('#f5f5f5', 0.5) }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        Esta variável usará o mapeamento global definido nas configurações.
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Não é necessário configurar valores adicionais aqui.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Stack>
                            ) : (
                                <Alert severity="info">
                                    Nenhuma variável encontrada no título ou na mensagem final. 
                                    Adicione variáveis utilizando {"{nome_da_variavel}"} no título ou na mensagem final.
                                </Alert>
                            )}
                        </Paper>
                    </TabPanel>
                </Box>
            </DialogContent>
            <DialogActions sx={{ 
                p: 3, 
                borderTop: '1px solid',
                borderColor: theme.palette.divider
            }}>
                <Button 
                    onClick={onClose}
                    variant="outlined"
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none'
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleSaveClick}
                    variant="contained"
                    disabled={!isSaveEnabled}
                    sx={{ 
                        borderRadius: 2,
                        px: 3,
                        textTransform: 'none'
                    }}
                >
                    Salvar
                </Button>
            </DialogActions>
        </>
    );

    // Renderizar o componente
    return useExternalDialog ? (
        renderContent()
    ) : (
        <Dialog 
            open={open}
            maxWidth="md"
            fullWidth
            onClick={(e) => e.stopPropagation()}
        >
            {renderContent()}
        </Dialog>
    );
}); 

// Exportação padrão do QuestionEditor (opcional, se necessário)
export const QuestionEditor = {
    QuestionForm,
    ConclusionForm
}; 